import { isBusinessDay } from "@milledon/lib";

import { formatGrams, formatNumber } from "~/lib/format";
import type {
  AdminCampaignQuotaRow,
  AdminInventoryQuery,
  AdminStockPoolRow,
  CorrectStockCapacityRequest,
  CorrectStockCapacityResponse,
  DailyCapacitySource,
  RecordPhysicalShortageRequest,
  RecordPhysicalShortageResponse,
  SetCampaignQuotaCapacityRequest,
  SetCampaignQuotaCapacityResponse,
  SetStockAcceptingRequest,
  SetStockAcceptingResponse,
} from "~/types/admin-inventory";

const MAX_DAILY_RANGE_DAYS = 62;
const MAX_CAPACITY = 2_147_483_647;
// API は Array.from(reason).length、DB は char_length(...) で数える。
// UTF-16 単位の String#length で数えるとサロゲートペアを含む正当な理由が
// UI だけで弾かれるため、同じ「符号位置数」で揃える。
const MAX_REASON_LENGTH = 500;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_INPUT_PATTERN = /^(\d{4})-(\d{2})$/;

export const capacitySourceLabels: Readonly<
  Record<DailyCapacitySource, string>
> = {
  stored: "保存済み",
  default: "既定値",
};

// 割当ロジック（shipDateCandidates）と同じ土日祝判定。休業日にも既定値の
// 行は表示されるが、候補生成が除外するため割当は乗らない — その区別を
// 画面でも示すための表示専用判定。
export function isShippingHoliday(allocationDate: string): boolean {
  if (!ISO_DATE_PATTERN.test(allocationDate)) {
    return false;
  }
  return !isBusinessDay(allocationDate);
}

export const acceptingNewLabels: Readonly<Record<"true" | "false", string>> = {
  true: "受付中",
  false: "停止中",
};

function parseIsoDate(value: string): number | null {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? timestamp
    : null;
}

function formatIsoDate(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

export function getInitialJstDateRange(now = new Date()): AdminInventoryQuery {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "";
  const dailyFrom = `${part("year")}-${part("month")}-${part("day")}`;
  const fromTimestamp = parseIsoDate(dailyFrom);
  if (fromTimestamp === null) {
    throw new Error("JST の日付を取得できませんでした。");
  }
  return {
    daily_from: dailyFrom,
    daily_to: formatIsoDate(fromTimestamp + 30 * 86_400_000),
  };
}

export type DailyDateRangeValidation =
  | { ok: true }
  | { ok: false; reason: "invalid_date" | "from_after_to" | "range_too_large" };

export function validateDailyDateRange(
  dailyFrom: string,
  dailyTo: string,
): DailyDateRangeValidation {
  const from = parseIsoDate(dailyFrom);
  const to = parseIsoDate(dailyTo);
  if (from === null || to === null)
    return { ok: false, reason: "invalid_date" };
  if (from > to) return { ok: false, reason: "from_after_to" };
  const days = Math.floor((to - from) / 86_400_000) + 1;
  return days <= MAX_DAILY_RANGE_DAYS
    ? { ok: true }
    : { ok: false, reason: "range_too_large" };
}

export function buildAdminInventoryQuery(query: AdminInventoryQuery): string {
  const search = new URLSearchParams({
    daily_from: query.daily_from,
    daily_to: query.daily_to,
  });
  return `?${search.toString()}`;
}

export function normalizeMonthInput(value: string): string | null {
  const match = MONTH_INPUT_PATTERN.exec(value);
  if (!match) return null;
  const candidate = `${match[1]}-${match[2]}-01`;
  return parseIsoDate(candidate) === null ? null : candidate;
}

/**
 * Dialog が保持する月次枠は「開いた時点」の snapshot なので、409 競合後に
 * 一覧を再取得しても expected が古いままだと再プレビューがまた 409 になる。
 * 自然キー (campaign_id, month) で最新行へ解決し、設計書 §5-4 の競合回復
 * （最新値でもう一度プレビュー）を Dialog を開き直さずに成立させる。
 */
export function resolveCampaignQuotaRow(
  target: AdminCampaignQuotaRow,
  rows: readonly AdminCampaignQuotaRow[],
): AdminCampaignQuotaRow {
  return findCampaignQuotaRow(rows, target.campaign_id, target.month) ?? target;
}

export function findCampaignQuotaRow(
  rows: readonly AdminCampaignQuotaRow[],
  campaignId: string,
  month: string,
): AdminCampaignQuotaRow | undefined {
  return rows.find(
    (row) => row.campaign_id === campaignId && row.month === month,
  );
}

export type CampaignQuotaForm =
  | {
      mode: "create";
      campaignId: string;
      monthInput: string;
      newCapacityInput: string;
      reason: string;
    }
  | {
      mode: "update";
      quota: AdminCampaignQuotaRow;
      newCapacityInput: string;
      reason: string;
    };

export type CampaignQuotaFormValidation =
  | { ok: true; request: SetCampaignQuotaCapacityRequest }
  | {
      ok: false;
      reason:
        "required" | "invalid_month" | "invalid_capacity" | "reason_too_long";
    };

export function buildCampaignQuotaRequest(
  form: CampaignQuotaForm,
): CampaignQuotaFormValidation {
  const campaignId =
    form.mode === "create"
      ? form.campaignId.trim()
      : form.quota.campaign_id.trim();
  const month =
    form.mode === "create"
      ? normalizeMonthInput(form.monthInput)
      : form.quota.month;
  const reason = form.reason.trim();
  if (!campaignId || !reason) return { ok: false, reason: "required" };
  if (Array.from(reason).length > MAX_REASON_LENGTH) {
    return { ok: false, reason: "reason_too_long" };
  }
  if (!month || parseIsoDate(month) === null || !month.endsWith("-01")) {
    return { ok: false, reason: "invalid_month" };
  }
  if (!/^\d+$/.test(form.newCapacityInput.trim())) {
    return { ok: false, reason: "invalid_capacity" };
  }
  const newCapacity = Number(form.newCapacityInput.trim());
  if (
    !Number.isSafeInteger(newCapacity) ||
    newCapacity < 0 ||
    newCapacity > MAX_CAPACITY
  ) {
    return { ok: false, reason: "invalid_capacity" };
  }

  return {
    ok: true,
    request: {
      campaign_id: campaignId,
      month,
      expected_capacity: form.mode === "create" ? null : form.quota.capacity,
      new_capacity: newCapacity,
      reason,
      dry_run: true,
    },
  };
}

export type CampaignQuotaState =
  | { phase: "idle"; conflict?: boolean }
  | { phase: "previewing" }
  | {
      phase: "ready";
      request: SetCampaignQuotaCapacityRequest;
      preview: SetCampaignQuotaCapacityResponse;
      confirmed: boolean;
    }
  | {
      phase: "executing";
      request: SetCampaignQuotaCapacityRequest;
      preview: SetCampaignQuotaCapacityResponse;
    }
  | {
      phase: "complete";
      preview: SetCampaignQuotaCapacityResponse;
      result: SetCampaignQuotaCapacityResponse;
    };

export type CampaignQuotaAction =
  | { type: "reset" }
  | { type: "input_changed" }
  | { type: "preview_started" }
  | {
      type: "preview_received";
      request: SetCampaignQuotaCapacityRequest;
      preview: SetCampaignQuotaCapacityResponse;
    }
  | { type: "preview_failed" }
  | { type: "confirm_toggled"; confirmed: boolean }
  | { type: "execute_started" }
  | { type: "execute_received"; result: SetCampaignQuotaCapacityResponse }
  | { type: "execute_failed" }
  | { type: "conflict_received" };

export const initialCampaignQuotaState: CampaignQuotaState = { phase: "idle" };

export function campaignQuotaReducer(
  state: CampaignQuotaState,
  action: CampaignQuotaAction,
): CampaignQuotaState {
  switch (action.type) {
    case "reset":
      return initialCampaignQuotaState;
    case "input_changed":
      return state.phase === "executing" ? state : initialCampaignQuotaState;
    case "preview_started":
      return { phase: "previewing" };
    case "preview_received":
      return {
        phase: "ready",
        request: action.request,
        preview: action.preview,
        confirmed: false,
      };
    case "preview_failed":
      return state.phase === "previewing" ? initialCampaignQuotaState : state;
    case "confirm_toggled":
      return state.phase === "ready"
        ? { ...state, confirmed: action.confirmed }
        : state;
    case "execute_started":
      return canExecuteCampaignQuota(state)
        ? { phase: "executing", request: state.request, preview: state.preview }
        : state;
    case "execute_received":
      return state.phase === "executing"
        ? { phase: "complete", preview: state.preview, result: action.result }
        : state;
    case "execute_failed":
      return state.phase === "executing"
        ? {
            phase: "ready",
            request: state.request,
            preview: state.preview,
            confirmed: false,
          }
        : state;
    case "conflict_received":
      return { phase: "idle", conflict: true };
  }
}

export function canExecuteCampaignQuota(
  state: CampaignQuotaState,
): state is Extract<CampaignQuotaState, { phase: "ready" }> {
  return (
    state.phase === "ready" &&
    state.confirmed &&
    state.preview.operation !== "noop"
  );
}

export function createExecuteRequest(
  state: CampaignQuotaState,
): SetCampaignQuotaCapacityRequest | null {
  if (state.phase !== "ready" && state.phase !== "executing") return null;
  return { ...state.request, dry_run: false };
}

export function formatInventoryGrams(value: number): string {
  return formatGrams(value);
}

export function formatCapacityCount(value: number): string {
  return `${formatNumber(value)} 件`;
}

export function resolveStockPoolRow(
  target: AdminStockPoolRow,
  rows: readonly AdminStockPoolRow[],
): AdminStockPoolRow {
  return rows.find((row) => row.id === target.id) ?? target;
}

type StockFormError =
  | "required"
  | "invalid_capacity"
  | "reason_too_long"
  | "not_a_reduction"
  | "too_many_cycles";

function parseCapacityInput(value: string): number | null {
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) return null;
  const capacity = Number(normalized);
  return Number.isSafeInteger(capacity) &&
    capacity >= 0 &&
    capacity <= MAX_CAPACITY
    ? capacity
    : null;
}

function normalizeReason(
  reason: string,
):
  | { ok: true; reason: string }
  | { ok: false; reason: "required" | "reason_too_long" } {
  const normalized = reason.trim();
  if (!normalized) return { ok: false, reason: "required" };
  if (getReasonLengthStatus(normalized).exceeded > 0) {
    return { ok: false, reason: "reason_too_long" };
  }
  return { ok: true, reason: normalized };
}

export function getReasonLengthStatus(reason: string): {
  count: number;
  remaining: number;
  exceeded: number;
} {
  const count = Array.from(reason.trim()).length;
  return {
    count,
    remaining: Math.max(0, MAX_REASON_LENGTH - count),
    exceeded: Math.max(0, count - MAX_REASON_LENGTH),
  };
}

export function physicalShortageRecoveryForErrorCode(
  code: string | null | undefined,
): "conflict" | "not_covered" | null {
  if (code === "shortage_not_covered") return "not_covered";
  if (
    code === "physical_shortage_conflict" ||
    code === "already_billed" ||
    code === "cycle_not_found" ||
    code === "cycle_not_on_pool" ||
    code === "stock_capacity_below_reserved"
  ) {
    return "conflict";
  }
  return null;
}

export type StockCapacityFormValidation =
  | { ok: true; request: CorrectStockCapacityRequest }
  | { ok: false; reason: Exclude<StockFormError, "not_a_reduction"> };

export function buildStockCapacityRequest(
  pool: AdminStockPoolRow,
  newCapacityInput: string,
  reasonInput: string,
): StockCapacityFormValidation {
  const reason = normalizeReason(reasonInput);
  if (!reason.ok) return reason;
  const capacity = parseCapacityInput(newCapacityInput);
  if (capacity === null) return { ok: false, reason: "invalid_capacity" };
  return {
    ok: true,
    request: {
      pool_id: pool.id,
      expected_capacity_brown_rice_g: pool.capacity_brown_rice_g,
      new_capacity_brown_rice_g: capacity,
      reason: reason.reason,
      dry_run: true,
    },
  };
}

export type StockAcceptingFormValidation =
  | { ok: true; request: SetStockAcceptingRequest }
  | { ok: false; reason: "required" | "reason_too_long" };

export function buildStockAcceptingRequest(
  pool: AdminStockPoolRow,
  reasonInput: string,
): StockAcceptingFormValidation {
  const reason = normalizeReason(reasonInput);
  if (!reason.ok) return reason;
  return {
    ok: true,
    request: {
      pool_id: pool.id,
      accepting: !pool.accepting_new,
      reason: reason.reason,
      dry_run: true,
    },
  };
}

export type PhysicalShortageFormValidation =
  | { ok: true; request: RecordPhysicalShortageRequest }
  | { ok: false; reason: StockFormError };

export function buildPhysicalShortageRequest(
  pool: AdminStockPoolRow,
  newCapacityInput: string,
  reasonInput: string,
  cycleIds: readonly string[] = [],
  stopAccepting = true,
): PhysicalShortageFormValidation {
  const reason = normalizeReason(reasonInput);
  if (!reason.ok) return reason;
  const capacity = parseCapacityInput(newCapacityInput);
  if (capacity === null) return { ok: false, reason: "invalid_capacity" };
  if (capacity >= pool.capacity_brown_rice_g) {
    return { ok: false, reason: "not_a_reduction" };
  }
  const uniqueCycleIds = [...new Set(cycleIds)];
  if (uniqueCycleIds.length > 100) {
    return { ok: false, reason: "too_many_cycles" };
  }
  return {
    ok: true,
    request: {
      pool_id: pool.id,
      expected_capacity_brown_rice_g: pool.capacity_brown_rice_g,
      new_capacity_brown_rice_g: capacity,
      cycle_ids: uniqueCycleIds,
      stop_accepting: stopAccepting,
      reason: reason.reason,
      dry_run: true,
    },
  };
}

export function physicalShortageSelection(
  preview: RecordPhysicalShortageResponse,
  cycleIds: readonly string[],
): { selected_release_g: number; covered: boolean } {
  const selected = new Set(cycleIds);
  const selectedRelease = preview.candidates.reduce(
    (total, candidate) =>
      total +
      (selected.has(candidate.cycle_id) ? candidate.brown_rice_eq_g : 0),
    0,
  );
  return {
    selected_release_g: selectedRelease,
    covered: selectedRelease >= preview.required_release_g,
  };
}

type OperationState<Request, Response> =
  | { phase: "idle"; recovery?: "conflict" | "not_covered" }
  | { phase: "previewing" }
  | { phase: "ready"; request: Request; preview: Response; confirmed: boolean }
  | { phase: "executing"; request: Request; preview: Response }
  | { phase: "complete"; preview: Response; result: Response };

type OperationAction<Request, Response> =
  | { type: "reset" | "input_changed" | "preview_started" | "preview_failed" }
  | { type: "preview_received"; request: Request; preview: Response }
  | { type: "confirm_toggled"; confirmed: boolean }
  | { type: "execute_started" }
  | { type: "execute_received"; result: Response }
  | { type: "execute_failed" }
  | { type: "conflict_received" }
  | { type: "not_covered_received" };

function operationReducer<Request, Response>(
  state: OperationState<Request, Response>,
  action: OperationAction<Request, Response>,
  executable: (state: OperationState<Request, Response>) => boolean,
): OperationState<Request, Response> {
  switch (action.type) {
    case "reset":
      return { phase: "idle" };
    case "input_changed":
      return state.phase === "executing" ? state : { phase: "idle" };
    case "preview_started":
      return { phase: "previewing" };
    case "preview_received":
      return {
        phase: "ready",
        request: action.request,
        preview: action.preview,
        confirmed: false,
      };
    case "preview_failed":
      return state.phase === "previewing" ? { phase: "idle" } : state;
    case "confirm_toggled":
      return state.phase === "ready"
        ? { ...state, confirmed: action.confirmed }
        : state;
    case "execute_started":
      return state.phase === "ready" && executable(state)
        ? { phase: "executing", request: state.request, preview: state.preview }
        : state;
    case "execute_received":
      return state.phase === "executing"
        ? { phase: "complete", preview: state.preview, result: action.result }
        : state;
    case "execute_failed":
      return state.phase === "executing"
        ? {
            phase: "ready",
            request: state.request,
            preview: state.preview,
            confirmed: false,
          }
        : state;
    case "conflict_received":
      return { phase: "idle", recovery: "conflict" };
    case "not_covered_received":
      return { phase: "idle", recovery: "not_covered" };
  }
}

export type StockCapacityState = OperationState<
  CorrectStockCapacityRequest,
  CorrectStockCapacityResponse
>;
export type StockCapacityAction = OperationAction<
  CorrectStockCapacityRequest,
  CorrectStockCapacityResponse
>;
export const initialStockCapacityState: StockCapacityState = { phase: "idle" };
export function stockCapacityReducer(
  state: StockCapacityState,
  action: StockCapacityAction,
): StockCapacityState {
  return operationReducer(state, action, (candidate) =>
    candidate.phase === "ready"
      ? candidate.confirmed && candidate.preview.result !== "noop"
      : false,
  );
}
export function canExecuteStockCapacity(state: StockCapacityState): boolean {
  return (
    state.phase === "ready" &&
    state.confirmed &&
    state.preview.result !== "noop"
  );
}

export type StockAcceptingState = OperationState<
  SetStockAcceptingRequest,
  SetStockAcceptingResponse
>;
export type StockAcceptingAction = OperationAction<
  SetStockAcceptingRequest,
  SetStockAcceptingResponse
>;
export const initialStockAcceptingState: StockAcceptingState = {
  phase: "idle",
};
export function stockAcceptingReducer(
  state: StockAcceptingState,
  action: StockAcceptingAction,
): StockAcceptingState {
  return operationReducer(state, action, (candidate) =>
    candidate.phase === "ready" ? candidate.preview.result !== "noop" : false,
  );
}
export function canExecuteStockAccepting(state: StockAcceptingState): boolean {
  return state.phase === "ready" && state.preview.result !== "noop";
}

export type PhysicalShortageState = OperationState<
  RecordPhysicalShortageRequest,
  RecordPhysicalShortageResponse
>;
export type PhysicalShortageAction =
  | OperationAction<
      RecordPhysicalShortageRequest,
      RecordPhysicalShortageResponse
    >
  | { type: "stop_accepting_toggled"; stopAccepting: boolean };
export const initialPhysicalShortageState: PhysicalShortageState = {
  phase: "idle",
};
export function physicalShortageReducer(
  state: PhysicalShortageState,
  action: PhysicalShortageAction,
): PhysicalShortageState {
  if (action.type === "stop_accepting_toggled") {
    return state.phase === "ready"
      ? {
          ...state,
          request: { ...state.request, stop_accepting: action.stopAccepting },
          confirmed: false,
        }
      : state;
  }
  return operationReducer(state, action, (candidate) =>
    candidate.phase === "ready"
      ? candidate.confirmed && candidate.preview.covered
      : false,
  );
}
export function canExecutePhysicalShortage(
  state: PhysicalShortageState,
): boolean {
  return state.phase === "ready" && state.confirmed && state.preview.covered;
}

export function createStockExecuteRequest<Request extends { dry_run: boolean }>(
  state: OperationState<Request, unknown>,
): Request | null {
  if (state.phase !== "ready" && state.phase !== "executing") return null;
  return { ...state.request, dry_run: false };
}
